import { Section } from '@/components/containers/section';
import { useEncounter } from '@/contexts/encounter-context';
import { Navigate } from '@/util/react-router-dom';
import {
  CreateMessageErrorType,
  GetMessagesForEncounterResponse,
  GetProviderResponseItem,
  Message,
} from '@/api/api.schemas';
import { formatDate } from '@/util/formats';
import styles from './messages-page.module.css';
import clsx from 'clsx';
import { useGetEncountersMessages } from '@/api/get-messages-for-encounter';
import { Banner } from '@/components/banner';
import { useTranslation } from '@/contexts/i18n-context';
import { TextArea } from '@/components/inputs/text-area';
import z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/form';
import { Button } from '@/components/button';
import { NavButton } from '@/components/navigation/nav-button';
import { useCreateEncountersMessages } from '@/api/create-message';
import { useEffect, useRef, useState } from 'react';
import { MaterialSymbolIcon } from '@/components/material-symbol-link';
import { MAX_FILE_COUNT_MESSAGE } from '@/util/constants';
import { Loader } from '@/components/loaders/loader';
import { useUpdateEncountersMessages } from '@/api/update-encounter-messages-to-read';
import { useGetProvider } from '@/api/get-provider-info';
import { FileInput } from '@/components/uploads/file-input';
import { InfoRow, TextDivider } from '@/components/encounter/info-row';
import { useMediaQuery } from '@/hooks/use-media-query';
import { groupBy } from 'lodash';
import { FileItem } from '@/components/uploads/file/file-upload';
import { PageContainer } from '@/components/containers/page-container';
import { ProviderImage } from '@/components/encounter/provider/provider-image';
import { ErrorField } from '@/components/error-field';
import { Img } from '@/components/img';
import { formatState } from '@/util/helpers';

const translationBase = 'visit.messaging';

const messageSchema = z.object({
  messageText: z.string(),
});

type FormValues = z.infer<typeof messageSchema>;

export function MessagesPage() {
  const { t } = useTranslation();
  const { encounter } = useEncounter();
  const {
    data,
    isFetching,
    refetch: refetchMessages,
  } = useGetEncountersMessages(encounter.id, {
    query: {
      placeholderData: (previousData) =>
        previousData ?? { messages: [], canPatientMessage: false, isTriagedOut: false },
    },
  });
  const { data: providerData, isFetching: isFetchingProviderInfo } = useGetProvider(
    encounter.assignedProviderId!,
    { encounterId: encounter.id },
    {
      query: {
        enabled: !!encounter.assignedProviderId,
      },
    },
  );

  const { data: approvedProviderData, isFetching: isFetchingApprovedProviderInfo } = useGetProvider(
    encounter.approvedProviderId!,
    { encounterId: encounter.id },
    {
      query: {
        enabled: !!encounter.approvedProviderId,
      },
    },
  );

  const { mutate: sendMessage, isPending } = useCreateEncountersMessages();
  const { mutate: readMessages } = useUpdateEncountersMessages();
  const isMobile = useMediaQuery('--screen-md');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(messageSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      messageText: '',
    },
  });

  const messageText = form.watch('messageText');
  const isSendDisabled = isPending || (!messageText?.trim() && selectedFiles.length === 0);

  const handleFilesSelected = (files: File[]) => {
    const newFiles = files.slice(0, MAX_FILE_COUNT_MESSAGE - selectedFiles.length);
    setSelectedFiles((prev) => [...prev, ...newFiles]);
    setUploadError(null);
    form.clearErrors();
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setUploadError(null);
  };

  const onSubmit = (data: FormValues) => {
    if (selectedFiles.length === 0 && !form.getValues('messageText').trim()) {
      form.setError('messageText', { message: t('common.errors.required') });
      return;
    }
    setUploadError(null);
    sendMessage(
      {
        encounterId: encounter.id,
        data: { ...data, files: selectedFiles },
      },
      {
        onSuccess: () => {
          refetchMessages();
          form.setValue('messageText', '');
          setSelectedFiles([]);
        },
        onError: (error) => {
          if (
            error.type === CreateMessageErrorType.ValidationFailed ||
            error.type === CreateMessageErrorType.InvalidImage
          ) {
            setUploadError(t(`${translationBase}.error.unsupportedFormat`));
            return;
          }

          setUploadError(t(`${translationBase}.error.upload`));
        },
      },
    );
  };

  useEffect(() => {
    if (!data) {
      return;
    }

    const messageIds = data.messages.reduce<number[]>((acc, mess) => {
      if (!mess.isRead) acc.push(mess.messageId);
      return acc;
    }, []);

    if (messageIds.length > 0) {
      readMessages({ encounterId: encounter.id, data: { messageIds } });
    }
  }, [data, readMessages, encounter.id]);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'instant' });
    }
  }, [isFetching, isFetchingProviderInfo, isFetchingApprovedProviderInfo]);

  if (isFetchingProviderInfo || isFetchingApprovedProviderInfo) {
    return <Loader type="large" />;
  }

  if (!data || !providerData) {
    return <Navigate to="/system-error" />;
  }

  const { messages, ...rest } = data;

  return (
    <PageContainer className={styles.pageContainer}>
      <MessageHeader
        encounterId={encounter.id}
        provider={providerData.provider}
        messagesMeta={rest}
      />
      <Section
        ref={messagesContainerRef}
        isFormSection={false}
        className={styles.messagesContainer}
        classNameSection={styles.section}
      >
        {Object.entries(
          groupBy(messages, (message) => formatDate(message.messageDate, { format: 'user' })),
        ).map(([date, messages]) => {
          return (
            <div key={date} className={styles.messagesGroup}>
              <p className={clsx('body-3', styles.date, styles.separator)}>{date}</p>
              {messages.map((message) => (
                <MessageContainer
                  key={message.messageId}
                  message={message}
                  providerName={
                    message.senderId === providerData.provider.id
                      ? providerData.provider.fullName
                      : message.senderId === approvedProviderData?.provider.id
                        ? t(`${translationBase}.approver`, {
                            name: approvedProviderData.provider.fullName,
                          })
                        : undefined
                  }
                />
              ))}
            </div>
          );
        })}
        {isFetching && <Loader type="small" />}
      </Section>
      <Form form={form} onSubmit={onSubmit} className={styles.chatSection}>
        {uploadError && <ErrorField error={{ type: 'validate', message: uploadError }} />}
        {selectedFiles.length > 0 && (
          <div className={styles.previewContainer}>
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className={clsx(
                  styles.previewWrapper,
                  !file.type.startsWith('image/') && styles.pdf,
                )}
              >
                <Button
                  type="button"
                  variant="textOnly"
                  onClick={() => removeFile(index)}
                  disabled={isPending}
                  className={styles.deleteButton}
                >
                  <MaterialSymbolIcon name="close" className={styles.deleteIcon} />
                </Button>
                {file.type.startsWith('image/') ? (
                  <Img
                    src={URL.createObjectURL(file)}
                    alt={`preview-${index}`}
                    className={styles.previewImage}
                  />
                ) : (
                  <FileItem
                    file={{
                      name: file.name,
                      tempId: file.name,
                      status: 'uploaded',
                    }}
                    truncateName
                  />
                )}
              </div>
            ))}
          </div>
        )}
        {data.canPatientMessage ? (
          <div className={styles.inputSection}>
            <div className={styles.fileInputButtons}>
              <FileInput
                acceptType="files"
                onFilesSelected={handleFilesSelected}
                multiple
                buttonProps={{
                  disabled: selectedFiles.length >= MAX_FILE_COUNT_MESSAGE || isPending,
                  variant: 'none',
                  children: <MaterialSymbolIcon name="attach_file" className={styles.iconButton} />,
                }}
              />
              <FileInput
                onFilesSelected={handleFilesSelected}
                multiple
                buttonProps={{
                  disabled: selectedFiles.length >= MAX_FILE_COUNT_MESSAGE || isPending,
                  variant: 'none',
                  children: <MaterialSymbolIcon name="image" className={styles.iconButton} />,
                }}
              />
            </div>
            <TextArea
              name="messageText"
              className={styles.inputContainer}
              rows={1}
              disabled={isPending}
              placeholder={t(`${translationBase}.input.placeholder${isMobile ? 'Mobile' : ''}`)}
            />
            <Button
              loading={isPending}
              disabled={isSendDisabled}
              type="submit"
              className={clsx(isMobile && styles.sendButton)}
            >
              {isMobile ? (
                <MaterialSymbolIcon name="send" />
              ) : (
                t(`${translationBase}.input.btnText`)
              )}
            </Button>
          </div>
        ) : (
          <p className={clsx('body-3', styles.endOfMessages)}>
            {t(`${translationBase}.endOfMessages`)}
          </p>
        )}
      </Form>
    </PageContainer>
  );
}

type MessagesMeta = Omit<GetMessagesForEncounterResponse, 'messages'>;

interface MessageHeaderProps {
  encounterId: number;
  provider: GetProviderResponseItem;
  messagesMeta: MessagesMeta;
}

function MessageHeader({ encounterId, provider, messagesMeta }: MessageHeaderProps) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('--screen-md');

  return (
    <div>
      <div className={styles.header}>
        <NavButton
          to={`/visit/${encounterId}/tx-plan/treatment`}
          className={styles.navButton}
          text={isMobile ? null : `${translationBase}.backToVisit`}
        />
        <div className={styles.providerInfo}>
          <ProviderImage url={provider.img} className={styles.providerImg} />
          <div className={styles.providerDetails}>
            <h1 className="bold">{provider.fullName}</h1>
            {provider.officeState && (
              <InfoRow
                icon="pin_drop"
                title={t(`${translationBase}.timeZone`)}
                contentClassName={styles.content}
                details={
                  <TextDivider
                    details={[provider.officeState.timeZone, formatState(provider.officeState)]}
                  />
                }
              />
            )}
          </div>
        </div>
      </div>
      <MessagingBanner messagesMeta={messagesMeta} />
    </div>
  );
}

interface MessagingBannerProps {
  messagesMeta: MessagesMeta;
}

function MessagingBanner({ messagesMeta }: MessagingBannerProps) {
  const { activePeriodEndDate, isTriagedOut, canPatientMessage } = messagesMeta;
  const { t } = useTranslation();

  if (!activePeriodEndDate) {
    return null;
  }

  return (
    <>
      {canPatientMessage && (
        <Banner
          type="success"
          className={styles.activeBanner}
          icon="hidden"
          contentClassName={styles.bannerContent}
        >
          <p className={styles.bannerText}>
            {t(`${translationBase}.activeBanner`, {
              date: formatDate(activePeriodEndDate, { format: 'user' }),
            })}
          </p>
        </Banner>
      )}
      {!canPatientMessage && (
        <Banner type="info" contentClassName={styles.bannerContent}>
          <span>
            {isTriagedOut
              ? t(`${translationBase}.triagedOutBanner`)
              : t(`${translationBase}.inactiveBanner`)}
          </span>
        </Banner>
      )}
    </>
  );
}

interface MessageContainerProps {
  message: Message;
  providerName?: string;
}

function MessageContainer({ message, providerName }: MessageContainerProps) {
  const { t } = useTranslation();

  return (
    <div className={clsx(styles.message, !providerName && styles.reverse)}>
      <div className={styles.messageDetails}>
        {message.messageText && (
          <p
            className={clsx(
              'body-3',
              'semibold',
              styles.messageText,
              providerName && styles.providerMessage,
            )}
          >
            {message.messageText}
          </p>
        )}

        {message.fileNames.length > 0 && (
          <div
            className={clsx(
              styles.messageText,
              providerName && styles.providerMessage,
              styles.fileContainer,
            )}
          >
            {message.fileNames.map((fileName, index) => (
              <FileItem
                key={`${message.messageId}-${index}`}
                className={styles.fileItem}
                file={{
                  name: fileName,
                  tempId: fileName,
                  status: 'uploaded',
                }}
                truncateName
              />
            ))}
          </div>
        )}

        {message.images.length > 0 && (
          <div className={clsx(styles.imagesContainer, !providerName && styles.reverse)}>
            {message.images.map((image) => (
              <Img key={image.id} alt={`message-${image.id}`} src={image.source} />
            ))}
          </div>
        )}
        <p className={clsx('body-3', styles.date)}>
          {providerName ?? t(`${translationBase}.you`)},{' '}
          {formatDate(message.messageDate, { format: 'system-time' })}
        </p>
      </div>
    </div>
  );
}
